import { Input } from '../../../Input';
import {
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconColumns3,
  IconFilter,
  IconFilterOff,
  IconSortAscending,
  IconSortDescending,
} from '@tabler/icons-react';
import { Select, SelectItem, SelectTriggerAction } from '../../../Select';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '../../../Dialog';
import { Badge } from '../../../Badge';
import { Button } from '../../../Button';
import { CellFilter } from './CellFilter';
import { SearchStatus } from './SearchStatus';
import { Popover, PopoverContent, PopoverTrigger } from '../../../Popover';
import { Tooltip } from '../../../Tooltip';
import { ToggleWithLabel } from '../../../ToggleWithLabel';
import { useTableContext } from '../../hooks/useTableContext';
import { useFullscreenTableContainer } from '../../hooks/useFullscreenTableContainer';
import { ReactNode, useMemo } from 'react';
import useI18n from '../../../../hooks/useI18n';

type FilterProps = {
  children?: ReactNode;
  actions?: ReactNode;
  dialogAriaDescribedby?: string;
  disableHideShowColumns?: boolean;
  disableFullscreen?: boolean;
};

export function Filter<TData>({
  children,
  actions,
  dialogAriaDescribedby,
  disableHideShowColumns = false,
  disableFullscreen = false,
}: FilterProps) {
  const { t } = useI18n();
  const sortByLabel = t('sortBy');
  const hideShowColumnsLabel = t('hideShowColumns');
  const {
    isFullscreen,
    setIsFullscreen,
    globalFilter,
    setGlobalFilter,
    table,
    tableState,
  } = useTableContext<TData>();
  const fullscreenContainer = useFullscreenTableContainer();

  const columns = table.getAllLeafColumns();
  const headerColumns = useMemo(() => {
    return columns.filter(
      (column): column is typeof column & { columnDef: { header: string } } =>
        typeof column.columnDef.header === 'string',
    );
  }, [columns]);

  const visibilityColumns = useMemo(() => {
    return headerColumns.filter(
      (column) => column.columnDef?.enableHiding !== false,
    );
  }, [headerColumns]);
  const sortingColumns = useMemo(() => {
    return headerColumns.filter(
      (column) => column.columnDef?.enableSorting !== false,
    );
  }, [headerColumns]);
  const sortingItems = useMemo(() => {
    return sortingColumns.map((column) => ({
      value: column.id,
      label:
        column.columnDef.meta?.headerLabelRenderer?.() ??
        column.columnDef.header,
    }));
  }, [sortingColumns]);

  const disableHideAll = useMemo(() => {
    return (
      visibilityColumns.filter(
        (column) => column.getCanHide() && column.getIsVisible(),
      ).length === 0
    );
  }, [visibilityColumns]);

  const hasActiveFilters = useMemo(
    () => Boolean(tableState.columnFilters?.length),
    [tableState.columnFilters],
  );

  const activeSort = tableState.sorting?.[0];
  const isSortDescending = activeSort?.desc ?? false;
  const sortDirectionActionLabel = isSortDescending
    ? t('sortAscending')
    : t('sortDescending');

  const toggleSortDirection = () => {
    if (!activeSort) return;
    table.setSorting([{ id: activeSort.id, desc: !activeSort.desc }]);
  };

  return (
    <div className='eye:my-24 eye:flex eye:flex-wrap eye:items-end eye:gap-l eye:gap-y-12'>
      <div className='eye:flex eye:flex-1 eye:flex-wrap eye:gap-16'>
        <div className='eye:min-w-[300px] eye:grow eye:lg:max-w-[350px]'>
          <Input
            label={t('search')}
            placeholder={t('searchTable')}
            value={globalFilter ?? ''}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className='eye:bg-white'
            labelProps={{ className: 'eye:bg-label-gradient-surface' }}
          />
          {/* `getRowCount()` is the `rowCount` option when the server pages
              the data, and the filtered rows otherwise. The filtered rows
              alone would be one page there. */}
          <SearchStatus
            query={globalFilter ?? ''}
            resultCount={table.getRowCount()}
          />
        </div>
        <div className='eye:min-w-[300px] eye:grow eye:lg:max-w-[350px]'>
          <Select
            label={sortByLabel}
            placeholder={sortByLabel}
            value={activeSort?.id ?? undefined}
            onValueChange={(value) => {
              const column = table.getColumn(value);
              table.setSorting([
                { id: value, desc: column?.columnDef.sortDescFirst ?? false },
              ]);
            }}
            slotProps={{
              trigger: {
                className: 'eye:bg-white',
                customTriggers: [
                  {
                    key: 'sort-direction',
                    render: () => (
                      <Tooltip content={sortDirectionActionLabel} asChild>
                        <SelectTriggerAction
                          size='icon'
                          disabled={!activeSort}
                          aria-label={sortDirectionActionLabel}
                          onClick={toggleSortDirection}
                        >
                          {isSortDescending ? (
                            <IconSortDescending aria-hidden={true} />
                          ) : (
                            <IconSortAscending aria-hidden={true} />
                          )}
                        </SelectTriggerAction>
                      </Tooltip>
                    ),
                  },
                ],
              },
              label: { className: 'eye:bg-label-gradient-surface' },
              portal: {
                container: fullscreenContainer,
              },
            }}
          >
            {sortingItems.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </Select>
        </div>
        {children && (
          <div className='eye:flex eye:max-h-screen'>{children}</div>
        )}
      </div>
      <div className='eye:flex eye:justify-end eye:gap-xs'>
        <div className='eye:flex eye:min-h-48 eye:items-center eye:justify-end'>
          {actions && <div className='eye:flex'>{actions}</div>}
          <Dialog>
            <DialogTrigger asChild className={'eye:mr-4xs'}>
              <Button
                variant='ghost'
                size='icon'
                className='eye:gap-4xs eye:px-2xs'
              >
                <Badge badgeContent={tableState.columnFilters.length}>
                  <span className='eye:flex eye:items-center eye:gap-4xs'>
                    <IconFilter aria-hidden={true} />
                    {t('filter')}
                  </span>
                </Badge>
              </Button>
            </DialogTrigger>
            <DialogContent
              aria-describedby={dialogAriaDescribedby}
              className='eye:max-w-[525px]'
            >
              <DialogTitle>{t('filterTable')}</DialogTitle>
              <CellFilter columns={headerColumns} />
              <DialogActions>
                {/* Disables itself on click, so it must keep focus in the
                    dialog (WCAG 2.4.3). */}
                <Button
                  variant='ghost'
                  focusableWhenDisabled
                  disabled={!hasActiveFilters}
                  onClick={() => table.resetColumnFilters(true)}
                >
                  <IconFilterOff />
                  {t('resetFilters')}
                </Button>
              </DialogActions>
            </DialogContent>
          </Dialog>
          {!disableHideShowColumns && (
            <Popover>
              <PopoverTrigger asChild={true}>
                <Tooltip content={hideShowColumnsLabel} asChild={true}>
                  <Button
                    size='icon'
                    variant='ghost'
                    aria-label={hideShowColumnsLabel}
                  >
                    <IconColumns3 aria-hidden={true} />
                  </Button>
                </Tooltip>
              </PopoverTrigger>
              <PopoverContent
                avoidCollisions={false}
                matchTriggerWidth={false}
                side='bottom'
                align='end'
              >
                <div className='eye:text-sm eye:flex eye:max-h-[400px] eye:flex-col eye:gap-12 eye:p-8'>
                  <div className={'eye:mb-12 eye:flex eye:gap-8'}>
                    <Button
                      disabled={disableHideAll}
                      variant={'outline'}
                      size={'sm'}
                      onClick={() => table.toggleAllColumnsVisible(false)}
                    >
                      {t('hideAll')}
                    </Button>
                    <Button
                      disabled={table.getIsAllColumnsVisible()}
                      variant={'outline'}
                      size={'sm'}
                      onClick={() => table.toggleAllColumnsVisible(true)}
                    >
                      {t('showAll')}
                    </Button>
                  </div>
                  {visibilityColumns.map((column) => (
                    <ToggleWithLabel
                      disabled={!column.getCanHide()}
                      key={column.id}
                      checked={column.getIsVisible()}
                      onCheckedChange={(checked) =>
                        column.toggleVisibility(checked)
                      }
                      label={
                        column.columnDef.meta?.headerLabelRenderer?.() ??
                        column.columnDef.header
                      }
                    />
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}
          {!disableFullscreen && (
            <Tooltip
              content={
                isFullscreen
                  ? t('deactivateFullScreen')
                  : t('activateFullScreen')
              }
              asChild
            >
              <Button
                variant={'ghost'}
                size={'icon'}
                aria-label={
                  isFullscreen
                    ? t('deactivateFullScreen')
                    : t('activateFullScreen')
                }
                onClick={() => setIsFullscreen((prev) => !prev)}
              >
                {isFullscreen ? (
                  <IconArrowsMinimize aria-hidden={true} />
                ) : (
                  <IconArrowsMaximize aria-hidden={true} />
                )}
              </Button>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}

Filter.displayName = 'Table.Filter';
